import React from "react";
import { Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import { IWandaTripInfo, TTrip } from "../../../../types/TripTypes";
import { IDownloadPdfTrips } from "../../../../types/DownloadPdfTypes";

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    marginBottom: 10,
  },
  card: {
    padding: 16,
    borderRadius: 10,
    marginBottom: 16,
  },
  normalText20: {
    fontSize: 20,
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
  },
  boldText14: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
  },
  boldGreenText14: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
    color: "#389E0D",
  },
  boldText16: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
  },
  normalText16: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
    color: "#2E2F32",
  },
  totalNormalText14: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
    color: "#2E2F32",
  },
  totalNormalText12: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.2,
    fontFamily: "Helvetica",
    color: "#2E2F32",
  },
  normalText14: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: "Helvetica",
  },
  tripTextColor: {
    color: "#2E2F32",
    width: 260,
  },
  legCostsAlign: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  overallCostAlign: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  solidDivider: {
    borderTop: "1px solid #D9D9D9",
    marginVertical: 8,
  },
  totalCostContainer: {
    paddingLeft: "16px",
  },
  costSeperationOrderDashed: {
    borderTop: "1px dashed #0063AF",
    marginVertical: 8,
  },
});

type prop = {
  tripTotalCost: number;
  wandaDiscountTotalCost: Function;
  trips: IDownloadPdfTrips;
  wandaTripInfo: IWandaTripInfo;
  wandaPromoCodeTotalCost: Function;
};

const DownloadPdfTotalTripCostDetails: React.FC<prop> = ({
  tripTotalCost,
  wandaDiscountTotalCost,
  trips,
  wandaTripInfo,
  wandaPromoCodeTotalCost,
}) => {
  const totalTripCost = trips.reduce((total, trip) => {
    return total + (trip.tripCost ?? 0);
  }, 0);

  const totalDiscountedAmountFixed: string = trips
    .reduce(
      (total, trip) => total + (trip.promoCodeDetail?.discountedAmount ?? 0),
      0
    )
    .toFixed(2);

  const totalWandaPlusDiscountCostFixed: string = trips
    .reduce((total, trip) => total + (trip.wandaPlusDiscountCost ?? 0), 0)
    .toFixed(2);

  return (
    <View style={styles.card}>
      <View style={[styles.row, styles.normalText20]}>
        <Text>Total</Text>
        <View style={{ flex: 1 }} />
      </View>

      <View style={[styles.row, styles.boldText14, styles.legCostsAlign]}>
        <Text>Trip ({trips?.length})</Text>
        <Text style={styles.normalText14}>${tripTotalCost.toFixed(2)}</Text>
      </View>

      <View style={styles.totalCostContainer}>
        {trips[0]?.wandaSubscriptionPlan !== "Basic" && (
          <View
            style={[
              styles.row,
              styles.totalNormalText14,
              styles.overallCostAlign,
            ]}
          >
            <Text>Wanda Plus Discount</Text>
            <Text style={styles.boldGreenText14}>
              {" "}
              {wandaTripInfo.wandaPlusDiscount * 100}%{"\u00A0\u00A0"}-$
              {totalWandaPlusDiscountCostFixed}
            </Text>
          </View>
        )}
        {trips[0].promoCodeDetail !== null && (
          <>
            <View
              style={[
                styles.row,
                styles.totalNormalText14,
                styles.overallCostAlign,
              ]}
            >
              <Text>Promo Code</Text>
              <Text style={styles.boldGreenText14}>
                {trips[0].promoCodeDetail?.discountType === "Percentage"
                  ? `${trips[0].promoCodeDetail?.discountValue}%`
                  : null}
                {"\u00A0\u00A0"}
                {`-$${totalDiscountedAmountFixed}`}{" "}
              </Text>
            </View>
            <View
              style={[
                styles.row,
                styles.totalNormalText12,
                styles.overallCostAlign,
              ]}
            >
              <Text>{trips[0]?.promoCodeDetail?.promoCode}</Text>
            </View>
          </>
        )}
        <View style={styles.solidDivider} />

        <View
          style={[styles.row, styles.normalText16, styles.overallCostAlign]}
        >
          <Text>Total cost</Text>
          <Text>${totalTripCost.toFixed(2)}</Text>
        </View>
        <Text style={[styles.normalText14, styles.tripTextColor]}>
          You’ll only be charged after each individual trip is complete.
        </Text>
      </View>
    </View>
  );
};

export default DownloadPdfTotalTripCostDetails;
